/**
 * Country-Specific Validation Functions
 * Comprehensive validation library for multiple countries
 */

import { registerValidation, ValidationResult } from './validation-registry';

// =============================================================================
// SOUTH AFRICA 🇿🇦
// =============================================================================

/**
 * South African ID Number Validation
 * 13-digit format with check digit algorithm
 */
export function validateSouthAfricanID(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'ID number is required' };
  }

  const cleaned = value.replace(/[^0-9]/g, '');

  if (cleaned.length !== 13) {
    return { isValid: false, message: 'South African ID must be 13 digits' };
  }

  // Extract components
  const dateOfBirth = cleaned.substring(0, 6);
  const gender = parseInt(cleaned.substring(6, 10));
  const citizenship = cleaned.substring(10, 11);
  const checkDigit = parseInt(cleaned.substring(12, 13));

  // Validate date of birth (YYMMDD)
  const month = parseInt(dateOfBirth.substring(2, 4));
  const day = parseInt(dateOfBirth.substring(4, 6));

  if (month < 1 || month > 12 || day < 1 || day > 31) {
    return { isValid: false, message: 'Invalid date of birth in ID number' };
  }

  // Luhn algorithm check digit validation
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const char = cleaned[i];
    if (!char) continue;
    let digit = parseInt(char);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }
  const calculatedCheckDigit = (10 - (sum % 10)) % 10;

  if (calculatedCheckDigit !== checkDigit) {
    return { isValid: false, message: 'Invalid ID number check digit' };
  }

  return {
    isValid: true,
    message: 'Valid South African ID number',
    details: {
      gender: gender < 5000 ? 'Female' : 'Male',
      citizenship: citizenship === '0' ? 'SA Citizen' : 'Permanent Resident',
    },
  };
}

// =============================================================================
// UNITED STATES 🇺🇸
// =============================================================================

/**
 * US Social Security Number Validation
 * XXX-XX-XXXX format with business rules
 */
export function validateUSSSN(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Social Security Number is required' };
  }

  const cleaned = value.replace(/[^0-9]/g, '');

  if (cleaned.length !== 9) {
    return { isValid: false, message: 'SSN must be 9 digits' };
  }

  const area = cleaned.substring(0, 3);
  const group = cleaned.substring(3, 5);
  const serial = cleaned.substring(5, 9);

  // Invalid area numbers
  if (area === '000' || area === '666' || area.startsWith('9')) {
    return { isValid: false, message: 'Invalid SSN area number' };
  }

  // Invalid group or serial numbers
  if (group === '00' || serial === '0000') {
    return { isValid: false, message: 'Invalid SSN group or serial number' };
  }

  return {
    isValid: true,
    message: 'Valid US Social Security Number',
    details: {
      formatted: `${area}-${group}-${serial}`,
      area: area,
      group: group,
      serial: serial,
    },
  };
}

/**
 * US Driver's License Number Validation (varies by state)
 */
export function validateUSDriversLicense(
  value: string,
  state?: string
): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: "Driver's license number is required" };
  }

  // Generic validation - states have different formats
  const cleaned = value.replace(/[^A-Za-z0-9]/g, '');

  if (cleaned.length < 4 || cleaned.length > 15) {
    return { isValid: false, message: "Invalid driver's license format" };
  }

  return {
    isValid: true,
    message: `Valid driver's license number${state ? ` for ${state}` : ''}`,
    details: { state: state || 'Unknown', cleaned },
  };
}

// =============================================================================
// UNITED KINGDOM 🇬🇧
// =============================================================================

/**
 * UK National Insurance Number Validation
 * AA123456A format
 */
export function validateUKNationalInsurance(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'National Insurance Number is required' };
  }

  const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();

  if (cleaned.length !== 9) {
    return {
      isValid: false,
      message: 'National Insurance Number must be 9 characters',
    };
  }

  // Valid prefixes (excluding certain combinations)
  const invalidPrefixes = ['BG', 'GB', 'NK', 'KN', 'TN', 'NT', 'ZZ'];
  const prefix = cleaned.substring(0, 2);

  if (invalidPrefixes.includes(prefix)) {
    return {
      isValid: false,
      message: 'Invalid National Insurance Number prefix',
    };
  }

  const pattern = /^[ABCEGHJKLMNOPRSTWXYZ][ABCEGHJKLMNPRSTWXYZ][0-9]{6}[A-D]$/;
  if (!pattern.test(cleaned)) {
    return {
      isValid: false,
      message: 'Invalid National Insurance Number format',
    };
  }

  return {
    isValid: true,
    message: 'Valid UK National Insurance Number',
    details: {
      formatted: `${prefix} ${cleaned.substring(2, 8)} ${cleaned.substring(8)}`,
      prefix: prefix,
    },
  };
}

/**
 * UK Postcode Validation
 */
export function validateUKPostcode(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Postcode is required' };
  }

  const cleaned = value.replace(/\s/g, '').toUpperCase();

  // UK postcode pattern
  const pattern = /^[A-Z]{1,2}[0-9R][0-9A-Z]?[0-9][ABD-HJLNP-UW-Z]{2}$/;

  if (!pattern.test(cleaned)) {
    return { isValid: false, message: 'Invalid UK postcode format' };
  }

  return {
    isValid: true,
    message: 'Valid UK postcode',
    details: {
      formatted:
        cleaned.length <= 6
          ? `${cleaned.substring(0, cleaned.length - 3)} ${cleaned.substring(cleaned.length - 3)}`
          : `${cleaned.substring(0, 4)} ${cleaned.substring(4)}`,
    },
  };
}

// =============================================================================
// CANADA 🇨🇦
// =============================================================================

/**
 * Canadian Social Insurance Number Validation
 * XXX-XXX-XXX format with check digit
 */
export function validateCanadianSIN(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Social Insurance Number is required' };
  }

  const cleaned = value.replace(/[^0-9]/g, '');

  if (cleaned.length !== 9) {
    return { isValid: false, message: 'SIN must be 9 digits' };
  }

  // Luhn algorithm check
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const char = cleaned[i];
    if (!char) continue;
    let digit = parseInt(char);
    if (i % 2 === 1) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
  }

  if (sum % 10 !== 0) {
    return { isValid: false, message: 'Invalid SIN check digit' };
  }

  return {
    isValid: true,
    message: 'Valid Canadian Social Insurance Number',
    details: {
      formatted: `${cleaned.substring(0, 3)}-${cleaned.substring(3, 6)}-${cleaned.substring(6, 9)}`,
    },
  };
}

/**
 * Canadian Postal Code Validation
 * A1A 1A1 format
 */
export function validateCanadianPostalCode(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Postal code is required' };
  }

  const cleaned = value.replace(/\s/g, '').toUpperCase();

  if (cleaned.length !== 6) {
    return {
      isValid: false,
      message: 'Canadian postal code must be 6 characters',
    };
  }

  const pattern =
    /^[ABCEGHJ-NPRSTVXY][0-9][ABCEGHJ-NPRSTV-Z][0-9][ABCEGHJ-NPRSTV-Z][0-9]$/;

  if (!pattern.test(cleaned)) {
    return { isValid: false, message: 'Invalid Canadian postal code format' };
  }

  return {
    isValid: true,
    message: 'Valid Canadian postal code',
    details: {
      formatted: `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)}`,
    },
  };
}

// =============================================================================
// AUSTRALIA 🇦🇺
// =============================================================================

/**
 * Australian Tax File Number Validation
 * XXX XXX XXX format with check digit
 */
export function validateAustralianTFN(value: string): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Tax File Number is required' };
  }

  const cleaned = value.replace(/[^0-9]/g, '');

  if (cleaned.length !== 9) {
    return { isValid: false, message: 'TFN must be 9 digits' };
  }

  // TFN check digit algorithm
  const weights = [1, 4, 3, 7, 5, 8, 6, 9, 10];
  let sum = 0;

  for (let i = 0; i < 8; i++) {
    const char = cleaned[i];
    const weight = weights[i];
    if (!char || weight === undefined) continue;
    sum += parseInt(char) * weight;
  }

  const remainder = sum % 11;
  const checkDigit = remainder < 2 ? remainder : 11 - remainder;

  const lastChar = cleaned[8];
  if (!lastChar || checkDigit !== parseInt(lastChar)) {
    return { isValid: false, message: 'Invalid TFN check digit' };
  }

  return {
    isValid: true,
    message: 'Valid Australian Tax File Number',
    details: {
      formatted: `${cleaned.substring(0, 3)} ${cleaned.substring(3, 6)} ${cleaned.substring(6, 9)}`,
    },
  };
}

// =============================================================================
// PHONE NUMBER VALIDATIONS
// =============================================================================

/**
 * International Phone Number Validation
 */
export function validatePhoneNumber(
  value: string,
  countryCode?: string
): ValidationResult {
  if (!value || typeof value !== 'string') {
    return { isValid: false, message: 'Phone number is required' };
  }

  const cleaned = value.replace(/[^0-9+]/g, '');

  // Basic international format validation
  if (cleaned.startsWith('+')) {
    if (cleaned.length < 8 || cleaned.length > 15) {
      return {
        isValid: false,
        message: 'Invalid international phone number length',
      };
    }
  } else {
    if (cleaned.length < 7 || cleaned.length > 15) {
      return { isValid: false, message: 'Invalid phone number length' };
    }
  }

  return {
    isValid: true,
    message: `Valid phone number${countryCode ? ` for ${countryCode}` : ''}`,
    details: { cleaned, countryCode },
  };
}

// =============================================================================
// REGISTRATION OF ALL VALIDATIONS
// =============================================================================

// Register all country-specific validations
registerValidation(
  'za_id_number',
  {
    type: 'identity',
    name: 'South African ID Number',
    format: '13 digits with check digit',
    example: '8001015009087',
    description: 'South African 13-digit identity number with Luhn check digit',
    country: 'ZA',
    category: 'identity',
  },
  validateSouthAfricanID,
  ['8001015009087', '9001010001081', '8505205077082']
);

registerValidation(
  'us_ssn',
  {
    type: 'identity',
    name: 'US Social Security Number',
    format: 'XXX-XX-XXXX',
    example: '123-45-6789',
    description:
      'US Social Security Number with area, group, and serial validation',
    country: 'US',
    category: 'identity',
  },
  validateUSSSN,
  ['123-45-6789', '456-78-9012', '789-01-2345']
);

registerValidation(
  'us_drivers_license',
  {
    type: 'license',
    name: "US Driver's License",
    format: 'Varies by state',
    example: 'D1234567',
    description: "US driver's license number (format varies by state)",
    country: 'US',
    category: 'license',
  },
  validateUSDriversLicense,
  ['D1234567', 'A123456789', 'DL123456']
);

registerValidation(
  'uk_nin',
  {
    type: 'identity',
    name: 'UK National Insurance Number',
    format: 'AA123456A',
    example: 'AB123456C',
    description: 'UK National Insurance Number with prefix validation',
    country: 'GB',
    category: 'identity',
  },
  validateUKNationalInsurance,
  ['AB123456C', 'JE123456D', 'MW123456A']
);

registerValidation(
  'uk_postcode',
  {
    type: 'address',
    name: 'UK Postcode',
    format: 'AA11 1AA or A11 1AA',
    example: 'SW1A 1AA',
    description: 'UK postal code with area and district validation',
    country: 'GB',
    category: 'address',
  },
  validateUKPostcode,
  ['SW1A 1AA', 'M1 1AA', 'B33 8TH']
);

registerValidation(
  'ca_sin',
  {
    type: 'identity',
    name: 'Canadian Social Insurance Number',
    format: 'XXX-XXX-XXX',
    example: '123-456-782',
    description: 'Canadian SIN with Luhn check digit validation',
    country: 'CA',
    category: 'identity',
  },
  validateCanadianSIN,
  ['123-456-782', '046-454-286', '130-692-544']
);

registerValidation(
  'ca_postal_code',
  {
    type: 'address',
    name: 'Canadian Postal Code',
    format: 'A1A 1A1',
    example: 'K1A 0A6',
    description: 'Canadian postal code with alternating letter-digit pattern',
    country: 'CA',
    category: 'address',
  },
  validateCanadianPostalCode,
  ['K1A 0A6', 'M5V 3L9', 'V6Z 2E5']
);

registerValidation(
  'au_tfn',
  {
    type: 'tax',
    name: 'Australian Tax File Number',
    format: 'XXX XXX XXX',
    example: '123 456 782',
    description: 'Australian TFN with weighted check digit algorithm',
    country: 'AU',
    category: 'tax',
  },
  validateAustralianTFN,
  ['123456782', '987654321', '111111116']
);

registerValidation(
  'phone_international',
  {
    type: 'contact',
    name: 'International Phone Number',
    format: '+XX XXXXXXXXXX',
    example: '+1 555-123-4567',
    description: 'International phone number with country code',
    category: 'contact',
  },
  validatePhoneNumber,
  ['+1 555-123-4567', '+44 20 7946 0958', '+27 11 123 4567']
);
